/**
 * Budget burndown of a CPM campaign: the remaining budget day by day, the pace
 * of the last complete days and when the money runs out at that pace. Shared by
 * the admin/audit "Ritmo do orçamento" card and the customer PDF; same rules as
 * the Analytics "Visão Geral" CPM cards.
 *
 * Days are São Paulo calendar days (YYYY-MM-DD). Daily spend comes from the
 * backend (`stats.daily_spend`), which spreads each approved clip's payout over
 * the days it gained paid views. Money that is on no day (withdrawn money kept
 * spent by a reversal, counter drift) is put on today, so the line always ends
 * at the balance the rest of the page shows.
 */

/** The pace is the mean daily spend of the last 7 complete days (today is still running). */
export const BURNDOWN_RATE_DAYS = 7;
/** Projections stop at about six months. */
export const BURNDOWN_MAX_PROJECTION_DAYS = 182;

export type BurndownSpendDay = { date: string; spendCents: number };
/** Views counted at the end of a day (cumulative), oldest first. */
export type BurndownViewsDay = { date: string; views: number };

export type BurndownInput = {
  totalCents: number;
  /** What the campaign says is left (the page's/PDF's "saldo disponível"). */
  remainingCents: number;
  startDate: string | null;
  endDate: string | null;
  /** Only an active campaign is projected. */
  active: boolean;
  today: string;
  dailySpend: BurndownSpendDay[];
  cumulativeViews?: BurndownViewsDay[];
};

export type BurndownOutlook =
  | { kind: 'runs-out'; date: string; days: number }
  | { kind: 'beyond-6-months' }
  | { kind: 'no-recent-spend'; days: number }
  | { kind: 'too-young' }
  | { kind: 'exhausted' }
  | { kind: 'inactive' };

export type BurndownPoint = {
  date: string;
  remainingCents: number;
  spendCents: number;
  /** the full-budget point the day before the first day */
  start?: true;
  today?: true;
};

export type BurndownWeek = {
  /** Monday */
  week: string;
  /** the current week, still running */
  partial: boolean;
  spendCents: number;
  /** views gained that week; null without a views series */
  views: number | null;
  /** balance at the end of the week (today for the current one) */
  remainingCents: number;
};

export type BudgetBurndown = {
  totalCents: number;
  spentCents: number;
  remainingCents: number;
  usedPercent: number | null;
  points: BurndownPoint[];
  /** [today, run-out (or six months ahead)], empty when nothing is projected */
  projection: Array<{ date: string; remainingCents: number }>;
  ratePerDayCents: number | null;
  ratePerWeekCents: number | null;
  /** complete days the pace was taken from (<= 7), null before a day has completed */
  rateDays: number | null;
  /** money on no day, added to today */
  adjustmentCents: number;
  outlook: BurndownOutlook;
  endDate: string | null;
  /** balance expected at the end date when the budget outlasts the campaign */
  remainingAtEndCents: number | null;
  weeks: BurndownWeek[];
};

const DAY_MS = 86_400_000;

function dayNumber(day: string): number {
  return Math.round(Date.parse(`${day}T00:00:00Z`) / DAY_MS);
}

export function addDays(day: string, days: number): string {
  return new Date((dayNumber(day) + days) * DAY_MS).toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  return dayNumber(to) - dayNumber(from);
}

/** The Monday on or before the day. */
export function mondayOf(day: string): string {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  return addDays(day, -((weekday + 6) % 7));
}

const SAO_PAULO_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
});

/** São Paulo calendar day of an instant; a plain `YYYY-MM-DD` stays as is. */
export function saoPauloDay(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const time = typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(time)) return null;
  return SAO_PAULO_DAY.format(time);
}

export function buildBudgetBurndown(input: BurndownInput): BudgetBurndown {
  const { totalCents, today } = input;
  const remainingCents = input.remainingCents;
  const spentCents = Math.max(0, totalCents - remainingCents);

  const spendByDay = new Map<string, number>();
  let summed = 0;
  for (const { date, spendCents } of input.dailySpend) {
    const day = date > today ? today : date;
    spendByDay.set(day, (spendByDay.get(day) ?? 0) + spendCents);
    summed += spendCents;
  }
  const adjustmentCents = Math.round(spentCents - summed);
  spendByDay.set(today, (spendByDay.get(today) ?? 0) + adjustmentCents);

  const firstSpend = input.dailySpend.length
    ? input.dailySpend.reduce((min, { date }) => (date < min ? date : min), input.dailySpend[0].date)
    : null;
  let first = input.startDate ?? firstSpend ?? today;
  if (firstSpend && firstSpend < first) first = firstSpend;
  if (first > today) first = today;

  const points: BurndownPoint[] = [
    { date: addDays(first, -1), remainingCents: totalCents, spendCents: 0, start: true },
  ];
  let running = totalCents;
  for (let day = first; day <= today; day = addDays(day, 1)) {
    const spendCents = spendByDay.get(day) ?? 0;
    running -= spendCents;
    points.push({ date: day, remainingCents: running, spendCents, ...(day === today ? { today: true as const } : {}) });
  }

  // Pace: the complete days of the last week, never before the first day.
  const from = addDays(today, -BURNDOWN_RATE_DAYS) > first ? addDays(today, -BURNDOWN_RATE_DAYS) : first;
  const rateDays = daysBetween(from, today);
  let ratePerDayCents: number | null = null;
  if (rateDays > 0) {
    let sum = 0;
    for (const { date, spendCents } of input.dailySpend) if (date >= from && date < today) sum += spendCents;
    ratePerDayCents = sum / rateDays;
  }

  let outlook: BurndownOutlook;
  const projection: BudgetBurndown['projection'] = [];
  let remainingAtEndCents: number | null = null;
  if (totalCents <= 0 || remainingCents <= 0) outlook = { kind: 'exhausted' };
  else if (!input.active) outlook = { kind: 'inactive' };
  else if (ratePerDayCents === null) outlook = { kind: 'too-young' };
  else if (ratePerDayCents <= 0) outlook = { kind: 'no-recent-spend', days: rateDays };
  else {
    const daysLeft = remainingCents / ratePerDayCents;
    if (daysLeft > BURNDOWN_MAX_PROJECTION_DAYS) {
      outlook = { kind: 'beyond-6-months' };
      projection.push(
        { date: today, remainingCents },
        {
          date: addDays(today, BURNDOWN_MAX_PROJECTION_DAYS),
          remainingCents: Math.round(remainingCents - ratePerDayCents * BURNDOWN_MAX_PROJECTION_DAYS),
        },
      );
    } else {
      const days = Math.round(daysLeft);
      outlook = { kind: 'runs-out', date: addDays(today, days), days };
      projection.push({ date: today, remainingCents }, { date: outlook.date, remainingCents: 0 });
    }
    const end = input.endDate;
    const runsPastEnd = outlook.kind === 'beyond-6-months' || (outlook.kind === 'runs-out' && end !== null && outlook.date > end);
    if (end !== null && end > today && runsPastEnd) {
      remainingAtEndCents = Math.round(remainingCents - ratePerDayCents * daysBetween(today, end));
    }
  }

  const views = input.cumulativeViews ? [...input.cumulativeViews].sort((a, b) => a.date.localeCompare(b.date)) : null;
  const viewsAt = (day: string): number => {
    if (!views) return 0;
    let value = 0;
    for (const point of views) {
      if (point.date > day) break;
      value = point.views;
    }
    return value;
  };
  const balanceAt = new Map(points.map((p) => [p.date, p.remainingCents]));
  const weeks: BurndownWeek[] = [];
  for (let week = mondayOf(first); week <= today; week = addDays(week, 7)) {
    const partial = week === mondayOf(today);
    const last = partial ? today : addDays(week, 6);
    let spendCents = 0;
    for (let day = week; day <= last; day = addDays(day, 1)) spendCents += spendByDay.get(day) ?? 0;
    weeks.push({
      week,
      partial,
      spendCents,
      views: views ? viewsAt(last) - viewsAt(addDays(week, -1)) : null,
      remainingCents: balanceAt.get(last) ?? totalCents,
    });
  }

  return {
    totalCents,
    spentCents,
    remainingCents,
    usedPercent: totalCents > 0 ? (spentCents / totalCents) * 100 : null,
    points,
    projection,
    ratePerDayCents,
    ratePerWeekCents: ratePerDayCents === null ? null : ratePerDayCents * 7,
    rateDays: rateDays > 0 ? rateDays : null,
    adjustmentCents,
    outlook,
    endDate: input.endDate,
    remainingAtEndCents,
    weeks,
  };
}

export type BurndownChartFrame = {
  /** the campaign end date, when it falls within the drawn range */
  end: string | null;
  /** the projection to draw: a budget lasting beyond six months stops at the campaign's end (or about a history ahead) */
  projection: BudgetBurndown['projection'];
  first: string;
  today: string;
  /** the last day on the x axis */
  last: string;
};

/** What the admin card and the PDF draw, so both charts agree. */
export function burndownChartFrame(burndown: BudgetBurndown): BurndownChartFrame {
  const first = burndown.points[0].date;
  const today = burndown.points[burndown.points.length - 1].date;
  const endDate = burndown.endDate;
  const end = endDate !== null && endDate >= first && daysBetween(today, endDate) <= BURNDOWN_MAX_PROJECTION_DAYS
    ? endDate : null;
  let projection = burndown.projection;
  if (projection.length === 2 && burndown.outlook.kind === 'beyond-6-months') {
    // Six months ahead would squash the history: stop at the campaign's end,
    // or about as far ahead as the history goes back (at least a month).
    const ahead = Math.min(BURNDOWN_MAX_PROJECTION_DAYS, Math.max(30, daysBetween(first, today)));
    const stop = end !== null && end > today ? end : addDays(today, ahead);
    const [from, to] = projection;
    const at = from.remainingCents
      + (to.remainingCents - from.remainingCents) * daysBetween(from.date, stop) / daysBetween(from.date, to.date);
    projection = [from, { date: stop, remainingCents: Math.round(at) }];
  }
  let last = today;
  for (const day of [projection[1]?.date, end]) if (day && day > last) last = day;
  return { end, projection, first, today, last };
}
